import React, { useContext } from "react";
import { Box, Typography, TextField, Paper, Alert, capitalize, AlertTitle } from "@mui/material";
import {
  Amount,
  BankingSyndicate,
  Chunk,
  Contact,
  MaturityDate,
  RetrievedLoanInformation,
  useDealContext,
  Vessel,
} from "@/contexts/DealContext";
import AmountInput from "@/components/ui/AmountInput";
import { Trans } from "@lingui/react/macro";
import BankingSyndicateReview from "./BankingSyndicatesreview";
import { organisationsMock } from "@/utils/bankingSyndicatesHelper";
import ExpandableAlert from "./ExpandableAlert";
import VesselReview from "./VesselsReview";
import ContactsReview from "./ContactsReview";
import ChipValidation from "./ChipInput";
import OrganizationMatcher from "./OrganizationMatcher";
import MaturityDateReview from "./MaturityDateReview";

type LoanValidationFieldProps = {
  data: RetrievedLoanInformation | Record<string, RetrievedLoanInformation>;
  keyName: string;
  onChange: (value: RetrievedLoanInformation | Amount) => void;
};

const LoanValidationField: React.FC<LoanValidationFieldProps> = ({ data, onChange, keyName }) => {
  const isAmount = "amount" in data && "currency" in data;
  const isVessels = keyName === "vessels";
  const isContacts = keyName === "contacts";
  const isAgent = keyName === "agent";  
  const isBankingSyndicates = keyName === "banking_syndicate";
  const isGuarantors = keyName === "guarantors";
  const isMaturityDate = keyName === "maturity_date";
  const isNestedObject = typeof data === "object" && !("value" in data) && !isAmount;
  const fields = isNestedObject ? Object.entries(data) : [["", data as RetrievedLoanInformation]];
  const { organizations } = useDealContext();
  if (isGuarantors) {
    console.log(data.value)
    if (data.value && typeof data.value === 'string' && data.value.length && !data.value.trim().startsWith('[')) {
      data.value = [data.value];
    }
  }
  const isNullGuarantors = (value?: string) => {
    try {
      return !value || 
          (value && (
            value === 'null' || 
            (typeof value === 'string' && value.trim().length === 0) || 
            (Array.isArray(value) && value.length === 0) ||
            (!Array.isArray(value) && Array.isArray(JSON.parse(value)) && JSON.parse(value).length === 0)
          ))
    } catch (error) {
      console.error("Error parsing data.value:", error);
      return true;
    }
  }
  return (
    <Paper elevation={0} sx={{ p: 0, mb: 1 }}>
      {data.chunk && (
        <ExpandableAlert
          title={(data.chunk && "metadata" in data.chunk && data.chunk?.metadata?.title) || "title"}
          chunk={data.chunk as Chunk}
        ></ExpandableAlert>
      )}
      {isAmount ? (
        <AmountInput value={data as Amount} onChange={onChange} />
      ) : isBankingSyndicates ? (
        <>
          <BankingSyndicateReview
            syndicates={data.value}
            organizations={organizations}
            onChange={(updated: BankingSyndicate[]) =>
              onChange({
                ...data,
                value: updated,
                chunk: data.chunk ? (data.chunk as Chunk) : { chunk: "" },
              })
            }
          />
        </>
      ) : isVessels ? (
        <>
          <VesselReview
            vessels={data.value || []}
            onChange={(updatedVessels: Vessel[]) =>
              onChange({
                ...data,
                value: updatedVessels,
                chunk: data.chunk ? (data.chunk as Chunk) : { chunk: "" },
              })
            }
          />
        </>
      ) : isContacts ? (
        <>
          <ContactsReview
            Contacts={data.value || []}
            onChange={(updatedContacts: Contact[]) =>
              onChange({
                ...data,
                value: updatedContacts,
                chunk: data.chunk ? (data.chunk as Chunk) : { chunk: "" },
              })
            }
          />
        </>
      ) : isGuarantors ? (
      <>
        {
          isNullGuarantors(data.value) ? (
          <Alert severity="info">
            <AlertTitle>No Guarantors Found</AlertTitle>
            <Trans>
              It seems like there are no guarantors associated with this loan. You will be able to add guarantors later if needed.
            </Trans>
          </Alert>
        ) : (
          <ChipValidation
            value={data.value}
            onChange={(value) => {
              onChange({
                ...data,
              value: value,
              chunk: data.chunk ? (data.chunk as Chunk) : { chunk: "" },
            });
          }}
        />
        )} 
      </>
      ) : isAgent ? (
        <OrganizationMatcher
          bank={data.value}
          allOrganizations={organizations}
          onValidate={(updatedAgent) =>
            onChange({
              ...data,
              value: {
                ...data.value,
                organization: updatedAgent.organization,
              },
              chunk: data.chunk ? (data.chunk as Chunk) : { chunk: "" },
            })
          }
        />
      ) : isMaturityDate ? (
        <MaturityDateReview 
          maturityDate={data.value as MaturityDate}
          onChange={(updatedMaturityDate) =>
            onChange({
              ...data,
              value: updatedMaturityDate,
              chunk: data.chunk ? (data.chunk as Chunk) : { chunk: "" },
            })
          }
        />
      ) : (
        fields.map(([subKey, fieldData]) => {
          const label = String(subKey) && subKey !== "" ? String(subKey) : keyName;
          const isValidField = typeof fieldData === "object" && "value" in fieldData;
          return (
            <Box key={String(subKey)} mt={1} mb={3}>
              {isValidField && fieldData.question && (
                <Alert severity="success" color="warning" icon={false} sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="warning.main">
                    <Trans>Answer or Add Details Below</Trans>
                  </Typography>
                  <Typography variant="body2" color="warning.main">
                    <Trans>
                      To help the model find the right information, you can enter the information or
                      answer the question below.
                    </Trans>
                  </Typography>
                </Alert>
              )}
              {isValidField && fieldData.value !== undefined ? (
                <TextField
                  fullWidth
                  label={capitalize(label.replaceAll("_", " "))}
                  variant="outlined"
                  value={fieldData.value || ""}
                  onChange={(e) => {
                    const toSend = { ...fieldData, value: e.target.value };
                    onChange({
                      ...data,
                      ...toSend,
                    });
                  }}
                />
              ) : (
                isValidField && (
                  <TextField
                    fullWidth
                    label={label.replaceAll("_", " ")}
                    variant="outlined"
                    value={fieldData || ""}
                    onChange={(e) =>
                      onChange({
                        ...fieldData,
                        value: e.target.value,
                      })
                    }
                  />
                )
              )}
            </Box>
          );
        })
      )}
    </Paper>
  );
};

export default LoanValidationField;
